import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Slider,
  Fade,
} from "@mui/material";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import RepeatIcon from "@mui/icons-material/Repeat";
import CloseIcon from "@mui/icons-material/Close";

import { useAudio } from "@/context/AudioProvider";
import NowPlayingViewSwitcher from "./NowPlayingViewSwitcher";

type Props = {
  track: any;
  isPlaying: boolean;
  progress: number;
  onClose: () => void;
};

const MobileNowPlayingModal: React.FC<Props> = ({
  track,
  isPlaying,
  progress,
  onClose,
}) => {
  const { togglePlay, seekTo, dispatch, state } = useAudio();
  const duration = (() => {
    if (track?.duration && typeof track.duration === "number") {
      // If duration is very large (> 3600), assume it's in milliseconds
      // Otherwise assume it's in seconds
      const isMilliseconds = track.duration > 3600;
      return isMilliseconds ? Math.floor(track.duration / 1000) : track.duration;
    }
    return 100; // fallback
  })();

  const [localProgress, setLocalProgress] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (track) {
      setLocalProgress(0);
    }
  }, [track?.id]);

  useEffect(() => {
    if (!isDragging) {
      setLocalProgress(progress);
    }
  }, [progress, isDragging]);

  const handleSliderChange = (
    event: Event | React.SyntheticEvent,
    newValue: number | number[]
  ) => {
    setIsDragging(true);
    setLocalProgress(newValue as number);
  };

  const handleSliderChangeCommitted = (
    event: Event | React.SyntheticEvent,
    newValue: number | number[]
  ) => {
    seekTo(newValue as number);
    setIsDragging(false);
  };

  const formatTime = (duration?: number | string): string => {
    if (!duration) return "00:00";
    
    // If it's already in MM:SS format (string), return as-is
    if (typeof duration === 'string' && duration.includes(':')) {
      return duration;
    }
    
    // Convert to number if it's a string
    const durationNum = typeof duration === 'string' ? parseFloat(duration) : duration;
    
    if (isNaN(durationNum)) return "00:00";
    
    // If duration is very large (> 3600), assume it's in milliseconds
    // Otherwise assume it's in seconds
    const isMilliseconds = durationNum > 3600;
    const seconds = isMilliseconds ? Math.floor(durationNum / 1000) : Math.floor(durationNum);
    
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <Fade in={true}>
      <Box
        sx={{
          position: "fixed",
          bottom: "56px",
          left: 0,
          width: "100%",
          height: "80vh",
          backgroundColor: "#1b1324",
          color: "white",
          padding: "10px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        {/* Close Button */}
        <IconButton
          sx={{ position: "absolute", top: 10, right: 10, color: "white" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        {/* Artwork/Lyrics Toggle View */}
        <Box
          sx={{
            width: "80%",
            aspectRatio: "1",
            borderRadius: "15px",
            marginBottom: "auto",
            overflow: "hidden",
            border: "0.5px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <NowPlayingViewSwitcher track={track} />
        </Box>

        {/* Song Info */}
        <Typography variant="h5" sx={{ mb: 1 }}>
          {track?.title || "Unknown Title"}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {track?.artistName || "Unknown Artist"}
        </Typography>

        {/* Time Display */}
        <Box
          sx={{ display: "flex", width: "80%", justifyContent: "space-between", mb: 1 }}
        >
          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
            {formatTime(localProgress)}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
            {formatTime(duration)}
          </Typography>
        </Box>

        {/* Seek Bar */}
        <Slider
          size="small"
          value={localProgress}
          max={duration}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderChangeCommitted}
          sx={{ color: "#E4FF1A", width: "80%" }}
        />

        {/* Playback Controls */}
        <Box
          sx={{ display: "flex", alignItems: "center", mt: 2, gap: 2 }}
        >
          <IconButton
            color="inherit"
            onClick={() => dispatch({ type: "TOGGLE_SHUFFLE" })}
          >
            <ShuffleIcon sx={{ color: state.isShuffling ? "#E4FF1A" : "inherit" }} />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={() => dispatch({ type: "PREV_TRACK" })}
          >
            <SkipPreviousIcon fontSize="large" />
          </IconButton>
          <IconButton color="inherit" onClick={togglePlay}>
            {isPlaying ? (
              <PauseIcon fontSize="large" />
            ) : (
              <PlayArrowIcon fontSize="large" />
            )}
          </IconButton>
          <IconButton
            color="inherit"
            onClick={() => dispatch({ type: "NEXT_TRACK" })}
          >
            <SkipNextIcon fontSize="large" />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={() => dispatch({ type: "TOGGLE_LOOP" })}
          >
            <RepeatIcon sx={{ color: state.isLooping ? "#E4FF1A" : "inherit" }} />
          </IconButton>
        </Box>
      </Box>
    </Fade>
  );
};

export default MobileNowPlayingModal;
